import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, take, tap } from 'rxjs';
import { AdobePageName } from '../../../services/adobe-launch/adobe-launch.pages';
import { AdobeLaunchService } from '../../../services/adobe-launch/adobe-launch.service';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { ButtonModule } from '@vg-constellation/angular-20/button';

import { CONSTANTS } from '../../../../common/constants/constants';
import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import { Content } from '../../../../common/interfaces/content.interface';
import { ViewType } from '../../../interfaces/view-type.enum';
import { MapAccountTypePipe } from '../../../pipes/map-account-type/map-account-type.pipe';
import { MapRoleTypePipe } from '../../../pipes/role-type/map-role-type.pipe';
import { PaginationProp, PaginatorService } from '../../../services/paginator/paginator.service';
import { TitleService } from '../../../services/title/title.service';
import { UsersService } from '../../../services/users/users.service';
import { updateSelectedViewTypeForUsers } from '../../../store/actions/view-type.action';
import {
    getSelectedViewTypeForUsers,
    selectUsersWithNames,
} from '../../../store/selectors/account-permissions.selector';
import { ContentUtil } from '../../../utils/content/content.util';
import contentUtils from '../../../utils/content/content-format.util';
import { Sort } from '../../../utils/sort/sort.interfaces';
import sortUtils from '../../../utils/sort/sort.util';
import { TableColumn } from '../../table/table.interface';
import { UserTileComponent, UserTileInputDataProps } from '../../tiles/user-tile/user-tile.component';
import { TabComponent } from '../tab.component';
import { getUsersTableCols, UsersTableProps, UserTableRow } from './users-table.config';
import { TableComponent } from '../../table/table.component';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ViewSelectorComponent } from '../../view-selector/view-selector.component';

@Component({
    selector: 'zci-users',
    templateUrl: './users.component.html',
    providers: [PaginatorService],
    imports: [
        AsyncPipe,
        NgIf,
        NgFor,
        FormsModule,
        TableComponent,
        ViewSelectorComponent,
        UserTileComponent,
        ButtonModule,
        RouterLink,
    ],
})
export class UsersComponent extends TabComponent implements OnInit, OnDestroy {
    private readonly paginatorService = inject(PaginatorService);
    private readonly store = inject(Store);
    private readonly titleService = inject(TitleService);
    private readonly mapAccountTypePipe = inject(MapAccountTypePipe);
    private readonly mapRoleTypePipe = inject(MapRoleTypePipe);
    private readonly adobeLaunchService = inject(AdobeLaunchService);
    private readonly usersService = inject(UsersService);

    content: Content = ContentUtil.content;

    readonly changeAccessRoute = '/' + APPLICATION_ROUTES.EDIT_USER_ACCESS.FULL_PATH;

    usrs$: Observable<UserTileInputDataProps[]>;

    sortedUsers$: Observable<UserTileInputDataProps[]>;

    usrsDataTable$: Observable<UserTableRow[]>;

    usrsPaginated: PaginationProp<UserTileInputDataProps>;

    usrsTableCols: TableColumn[] = getUsersTableCols(this.content.home.userTable.headers);

    private readonly subTitle: string = this.content.home.accountsNavTab.usersItemTitle;

    private destroy$: Subject<void> = new Subject();

    get loadMoreUsersLabel$(): Observable<string> {
        return this.usrsPaginated.nextCount$.pipe(
            map((count: number) => {
                return contentUtils.getContentWithDynamicNumber(
                    this.content.home.loadMoreUsers,
                    count,
                );
            }),
        );
    }

    get destroy(): Subject<void> {
        return this.destroy$;
    }

    ngOnInit(): void {
        this.usrs$ = this.store.select(selectUsersWithNames);

        this.titleService.setTitle(this.subTitle);

        this.usrsDataTable$ = this.usrs$.pipe(map(this.mapToUserTableRow.bind(this)));

        const { hasMore$, isLoading$, nextCount$ } = this.paginatorService;

        this.sortedUsers$ = this.usrs$.pipe(map(this.sortUsersForCardView.bind(this)));

        const data$ = this.paginatorService.initPaginator<UserTileInputDataProps>(
            this.sortedUsers$,
            CONSTANTS.PAGINATOR_PAGE_SIZE,
        );

        this.usrsPaginated = {
            data$,
            hasMore$,
            isLoading$,
            nextCount$,
            loadMore: () => this.paginatorService.loadMore(),
        };

        this.restoreViewTypeSelection();
        this.subscribeOnBreakpointChange();
    }

    ngOnDestroy(): void {
        this.saveViewTypeSelection();
        this.destroy$.next();
        this.destroy$.complete();
    }

    sortUsersForCardView(users: UserTileInputDataProps[]): UserTileInputDataProps[] {
        return [...users].sort((u1, u2) => {
            const nameParts1 = u1.usrName.split(' ');
            const nameParts2 = u2.usrName.split(' ');

            if (nameParts1.length < 2 || nameParts2.length < 2) {
                return u1.usrName.localeCompare(u2.usrName);
            }

            const firstNameCompare = nameParts1[0].localeCompare(nameParts2[0]);

            if (firstNameCompare !== 0) {
                return firstNameCompare;
            }

            return nameParts1[1].localeCompare(nameParts2[1]);
        });
    }

    protected saveViewTypeSelection(): void {
        this.store.dispatch(updateSelectedViewTypeForUsers(this.viewType));
    }

    protected restoreViewTypeSelection(): void {
        this.store
            .select(getSelectedViewTypeForUsers)
            .pipe(take(1), tap(this.trackPageLoad))
            .subscribe((viewType) => {
                this.viewType = viewType;
                this.changeDetector.detectChanges();
            });
    }

    private trackPageLoad = (viewType: ViewType): void => {
        const pageName: AdobePageName = this.adobeLaunchService.isAdobeInitialized
            ? AdobePageName.USERS_PAGE
            : AdobePageName.USERS_HOME_PAGE;

        this.adobeLaunchService.pageLoad(pageName, this.getViewTypeForPageLoad(viewType));
    };

    private mapToUserTableRow(users: UserTileInputDataProps[]): UserTableRow[] {
        return users.map((user: UserTileInputDataProps) => ({
            [UsersTableProps.UsrName]: user.usrName,
            [UsersTableProps.AccName]: user.accName,
            [UsersTableProps.RoleType]: this.mapRoleTypePipe.transform(user.roleType),
            [UsersTableProps.AccType]: this.mapAccountTypePipe.transform(user.accType),
            [UsersTableProps.Actions]: {
                navOverflowMenuConfig: this.usersService.getNavOverflowMenu(false),
            },
        }));
    }

    onColumnSort(event: Sort) {
        this.usrsDataTable$ = this.usrsDataTable$.pipe(
            map((users: UserTableRow[]) => {
                return sortUtils.ascDescSortingBySortEvent<UserTableRow>(users, event);
            }),
        );
    }
}